import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Divider,
    Button,
    Select,
    MenuItem,
    Card, // Added Card component
    CardContent,
    Paper, // Added Paper component for elevated appearance
    Stack // Added Stack for better spacing management
} from "@mui/material";
import { IconButton } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import { Calendar } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import { isValid, format } from "date-fns";
import { Alert } from "@mui/material";
import { getCollectionDetails } from "../../core/actions/reportDownloadAction";
import collectionSummaryServices from "../../core/services/reportDownloadServices";
import doctorActions from "../../core/actions/doctorActions";
import { generateCollectionDetailsExcel } from "../../utils/generateInvoiceExcel";

/* -------------------- Summary Row -------------------- */
const SummaryRow = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold">
            ₹ {value}
        </Typography>
    </Box>
);

export default function UserCollectionCard({ onCloseMenu }) {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(true);
    const [ServerRes, setServerRes] = useState({ success: "", error: "" });
    const [showMsgStrip, setShowMsgStrip] = useState(false);

    /* ================= REDUX ================= */
    const collectionSummary = useSelector((state) => state.collectionSummary);
    const summary = collectionSummary?.data?.erpResp?.customerPaymentInfo?.[0];

    /* ================= STAFF ================= */
    const [staffList, setStaffList] = useState([]);
    const [selectedPerson, setSelectedPerson] = useState("ALL");
    const COLLECTION_ALLOWED_TYPES = [
        "registrationdesk"
    ];

    const [selectedDepartment, setSelectedDepartment] = useState("");

    useEffect(() => {
        dispatch(
            doctorActions.getStaffList((resCb) => {
                if (resCb?.accresp?.personList) {
                    const staffOnly = resCb.accresp.personList.filter(
                        (p) => String(p.type).toLowerCase() !== "doctor"
                    );

                    setStaffList(staffOnly);

                    const loggedInUserId = JSON.parse(
                        localStorage.getItem("userId")
                    );

                    const matchedUser = staffOnly.find(
                        (p) => p.userId === loggedInUserId
                    );

                    if (matchedUser) {
                        setSelectedPerson(matchedUser.userId);
                    }
                }
            })
        );
    }, [dispatch]);


    /* ================= CALENDAR ================= */
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarMode, setCalendarMode] = useState("single");
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    const toggleCalendar = () => setShowCalendar((p) => !p);

    const clearCalendarSelection = () => {
        setSelectedDate(null);
        setDateRange({ start: null, end: null });
        setCalendarMode("single");
    };

    const handleCalendarChange = (value) => {
        // RANGE
        if (Array.isArray(value)) {
            const [start, end] = value;
            if (!isValid(start) || !isValid(end)) return;

            setDateRange({ start, end });
            setSelectedDate(null);
            setShowCalendar(false);
            return;
        }

        // SINGLE
        if (calendarMode === "single" && isValid(value)) {
            setSelectedDate(value);
            setDateRange({ start: value, end: value });
            setShowCalendar(false);
        }
    };

    const departmentList = useMemo(() => {
        return [
            ...new Set(
                staffList
                    .filter(
                        (person) =>
                            person.type &&
                            COLLECTION_ALLOWED_TYPES.includes(
                                person.type.toLowerCase()
                            )
                    )
                    .map((person) => person.type.toLowerCase())
            ),
        ];
    }, [staffList]);

    const getUsersByDepartment = (department) => {
        if (!department) return [];

        return staffList
            .filter(
                (person) =>
                    person.type &&
                    person.type.toLowerCase() === department.toLowerCase()
            )
            .map((person) => person.userId);
    };
    /* ================= DOWNLOAD ================= */
    const handleDownload = () => {
        var ServerResVal = ServerRes;
        const start =
            calendarMode === "single"
                ? selectedDate
                : dateRange.start;

        const end =
            calendarMode === "single"
                ? selectedDate
                : dateRange.end;

        if (!start || !end) {
            setServerRes({ success: "", error: "Please select a date" });
            return;
        }

        let usersToSend = [];
        if (selectedPerson === "ALL" && !selectedDepartment) {
            setServerRes({ success: "", error: "Please select a department" });
            return;
        }

        if (selectedPerson === "ALL") {
            if (selectedDepartment) {
                usersToSend = getUsersByDepartment(selectedDepartment);
            }
        } else {
            usersToSend = [selectedPerson];
        }

        if (selectedPerson === "ALL" && selectedDepartment && usersToSend.length === 0) {
            setServerRes({ success: "", error: "No users found for selected department" });
            return;
        }

        dispatch(
            getCollectionDetails(
                {
                    person: usersToSend,
                    startdate: dayjs(start).toISOString(),
                    enddate: dayjs(end).toISOString(),
                },
                async (res) => {
                    const apiResponse = res?.erpResp || {};
                    const rows = apiResponse?.customerPaymentInfo || [];
                    if (!rows.length) {
                        setServerRes({ success: "", error: "No data available" });
                        return;
                    }

                    const buffer = await generateCollectionDetailsExcel(apiResponse);
                    const blob = new Blob([buffer], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });

                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `Collection_Details_${Date.now()}.xlsx`;
                    link.click();
                    onCloseMenu?.()
                },
                onCloseMenu?.()
            )

        );
    };


    const closeAlert = () => {
        setServerRes({ success: "", error: "" });
    };


    /* ================= UI ================= */
    return (
        <>
            <div className="m-2">
                {ServerRes.success && (
                    <Alert severity="success" className="alert-div">
                        {ServerRes.success}{" "}
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={(event) => closeAlert(event)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    </Alert>
                )}
                {ServerRes.error && (
                    <Alert severity="error" className="alert-div">
                        {ServerRes.error}{" "}
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={(event) => closeAlert(event)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    </Alert>
                )}
            </div>
            <Box px={1.5} py={1} minWidth={260} position="relative" sx={{ overflow: "visible" }} >
                {/* SUMMARY */}
                {summary && (
                    <>
                        <SummaryRow
                            label="Net Cash"
                            value={summary.cashReceived - summary.cashRefunded}
                        />
                        <SummaryRow
                            label="Net Card"
                            value={summary.cardReceived - summary.cardRefunded}
                        />
                        <SummaryRow
                            label="Net UPI"
                            value={summary.upiReceived - summary.upiRefunded}
                        />
                    </>
                )}

                <Divider sx={{ my: 1 }} />

                {/* PERSON + CALENDAR */}
                <Box display="flex" alignItems="center" gap={1}>
                    {/* <Select
                        size="small"
                        value={selectedPerson}
                        fullWidth
                        onChange={(e) => setSelectedPerson(e.target.value)}
                        renderValue={(val) => (val === "ALL" ? "All" : val)}
                    >
                        <MenuItem value="ALL">All</MenuItem>
                        {staffList.map((p) => (
                            <MenuItem key={p.personId} value={p.userId}>
                                {p.userId} — {p.first_name}
                            </MenuItem>
                        ))}
                    </Select> */}
                    <Select
                        size="small"
                        value={selectedPerson}
                        fullWidth
                        onChange={(e) => {
                            const value = e.target.value;
                            setSelectedPerson(value);

                            if (value !== "ALL") {
                                setSelectedDepartment(""); // clear department if not ALL
                            }
                        }}
                        sx={{
                            height: 32,
                            fontSize: "0.85rem",
                            "& .MuiSelect-select": {
                                padding: "4px 8px",
                                display: "flex",
                                alignItems: "center"
                            }
                        }}
                    >
                        <MenuItem value="ALL">All</MenuItem>
                        {staffList.map((p) => (
                            <MenuItem
                                key={p.personId}
                                value={p.userId}
                                sx={{ fontSize: "0.85rem" }}
                            >
                                {p.first_name}
                            </MenuItem>
                        ))}
                    </Select>

                    <IconButton
                        size="small"
                        onClick={toggleCalendar}
                        sx={{
                            width: 32,
                            height: 32,
                            border: "1px solid #ddd",
                            borderRadius: "6px"
                        }}
                    >
                        <i className="material-icons" style={{ fontSize: 18 }}>
                            calendar_month
                        </i>
                    </IconButton>
                </Box>

                {/* CALENDAR POPUP */}
                {showCalendar &&
                    ReactDOM.createPortal(
                        <div
                            style={{
                                position: "fixed",
                                zIndex: 9999,
                                backgroundColor: "white",
                                padding: "8px",
                                borderRadius: "4px",
                                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                right: "20px",
                                top: "120px",
                            }}
                        >
                            {/* SAME CONTENT — NO UI CHANGE */}
                            <div className="d-flex justify-content-between mb-2">
                                <button
                                    className={`btn btn-sm ${calendarMode === "single"
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                        }`}
                                    onClick={() => setCalendarMode("single")}
                                >
                                    Single Date
                                </button>

                                <button
                                    className={`btn btn-sm ${calendarMode === "range"
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                        }`}
                                    onClick={() => setCalendarMode("range")}
                                >
                                    Date Range
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={clearCalendarSelection}
                                >
                                    Clear
                                </button>

                                <button
                                    className="btn btn-sm btn-light border-0"
                                    onClick={() => setShowCalendar(false)}
                                >
                                    <i className="material-icons">close</i>
                                </button>
                            </div>

                            <Calendar
                                onChange={handleCalendarChange}
                                selectRange={calendarMode === "range"}
                                maxDate={new Date()}
                                value={
                                    calendarMode === "single"
                                        ? selectedDate
                                        : dateRange.start && dateRange.end
                                            ? [dateRange.start, dateRange.end]
                                            : dateRange.start
                                }
                            />
                        </div>,
                        document.body
                    )}
                {selectedPerson === "ALL" && (
                    <Box mt={1}>
                        <Select
                            size="small"
                            fullWidth
                            value={selectedDepartment}
                            displayEmpty
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            renderValue={(val) =>
                                val ? val : "Select Department"
                            }
                            sx={{
                                height: 32,
                                fontSize: "0.85rem",
                                "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    display: "flex",
                                    alignItems: "center"
                                }
                            }}
                        >
                            {departmentList.map((dept) => (
                                <MenuItem key={dept} value={dept}>
                                    {dept.charAt(0).toUpperCase() + dept.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}

                {/* DOWNLOAD */}
                <Button
                    fullWidth
                    sx={{ mt: 1 }}
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                >
                    Download Report
                </Button>
            </Box>
        </>
    );

    //     return (
    //     <>
    //   {/* Alerts wrapped in a simple Box for consistent padding */}
    //   <Box m={2}> 
    //     {ServerRes.success && (
    //       <Alert severity="success" className="alert-div" action={
    //         <IconButton aria-label="close" color="inherit" size="small" onClick={(event) => closeAlert(event)}>
    //           <CloseIcon fontSize="inherit" />
    //         </IconButton>
    //       }>
    //         {ServerRes.success}
    //       </Alert>
    //     )}
    //     {ServerRes.error && (
    //       <Alert severity="error" className="alert-div" action={
    //         <IconButton aria-label="close" color="inherit" size="small" onClick={(event) => closeAlert(event)}>
    //           <CloseIcon fontSize="inherit" />
    //         </IconButton>
    //       }>
    //         {ServerRes.error}
    //       </Alert>
    //     )}
    //   </Box>

    //         {/* Using a Paper component with elevation for a distinct, modern look */}
    //         <Paper elevation={3} sx={{ p: 2, m: 2, minWidth: 280 }}>
    //             <Typography variant="h6" gutterBottom color="primary">
    //                 Financial Overview
    //             </Typography>
    //             {/* Use Stack for simple vertical spacing */}
    //             <Stack spacing={1}> 
    //                 {summary && (
    //                     <>
    //                         <SummaryRow
    //                             label="Net Cash"
    //                             value={summary.cashReceived - summary.cashRefunded}
    //                         />
    //                         <SummaryRow
    //                             label="Net Card"
    //                             value={summary.cardReceived - summary.cardRefunded}
    //                         />
    //                         <SummaryRow
    //                             label="Net UPI"
    //                             value={summary.upiReceived - summary.upiRefunded}
    //                         />
    //                     </>
    //                 )}
    //             </Stack>

    //             <Divider sx={{ my: 2 }} />

    //             {/* PERSON + CALENDAR in a flex container */}
    //             <Box display="flex" alignItems="center" gap={1} mb={2}>
    //                 <Select
    //                     size="small"
    //                     value={selectedPerson}
    //                     fullWidth
    //                     onChange={(e) => setSelectedPerson(e.target.value)}
    //                     renderValue={(val) => (val === "ALL" ? "All Staff" : val)} // Better label
    //                 >
    //                     <MenuItem value="ALL">All Staff</MenuItem>
    //                     {staffList.map((p) => (
    //                         <MenuItem key={p.personId} value={p.first_name}>
    //                             {p.first_name}
    //                         </MenuItem>
    //                     ))}
    //                 </Select>

    //                 <IconButton
    //                     onClick={toggleCalendar}
    //                     title="Select Date / Range"
    //                     color="primary"
    //                 >
    //                     <i className="material-icons">calendar_month</i>
    //                 </IconButton>
    //             </Box>

    //             {/* CALENDAR POPUP - kept original styling for functionality */}
    //             {showCalendar &&
    //                 ReactDOM.createPortal(
    //                     <div
    //                         style={{
    //                             position: "fixed", zIndex: 9999, backgroundColor: "white", padding: "8px", borderRadius: "4px",
    //                             boxShadow: "0 2px 10px rgba(0,0,0,0.15)", right: "20px", top: "120px",
    //                         }}
    //                     >
    //                         <div className="d-flex justify-content-between mb-2">
    //                             {/* ... calendar buttons using existing CSS classes ... */}
    //                         </div>

    //                         <Calendar
    //                             onChange={handleCalendarChange}
    //                             selectRange={calendarMode === "range"}
    //                             maxDate={new Date()}
    //                             value={
    //                                 calendarMode === "single"
    //                                     ? selectedDate
    //                                     : dateRange.start && dateRange.end
    //                                         ? [dateRange.start, dateRange.end]
    //                                         : dateRange.start
    //                             }
    //                         />
    //                     </div>,
    //                     document.body
    //                 )}


    //             {/* DOWNLOAD BUTTON prominent and at the bottom */}
    //             <Button
    //                 fullWidth
    //                 variant="contained"
    //                 startIcon={<DownloadIcon />}
    //                 onClick={handleDownload}
    //             >
    //                 Download Report
    //             </Button>
    //         </Paper>
    //     </>
    // );

}





// import React from "react";
// import { useSelector } from "react-redux";
// import { Box, Typography } from "@mui/material";

// const SummaryRow = ({ label, value }) => (
//   <Box display="flex" justifyContent="space-between" mb={0.5}>
//     <Typography variant="body2" color="text.secondary">
//       {label}
//     </Typography>
//     <Typography variant="body2" fontWeight="bold">
//       ₹ {value}
//     </Typography>
//   </Box>
// );

// export default function UserCollectionCard() {
//   // ✅ DEFINE IT HERE
//   const { data, loading } = useSelector(
//     (state) => state.collectionSummary
//   );

//   if (loading) {
//     return (
//       <Box px={2} py={1}>
//         <Typography variant="body2">Updating...</Typography>
//       </Box>
//     );
//   }

//   const summary =
//     data?.erpResp?.customerPaymentInfo?.[0];

//   if (!summary) return null;

//   const {
//     cashReceived = 0,
//     cashRefunded = 0,
//     cardReceived = 0,
//     cardRefunded = 0,
//     upiReceived = 0,
//     upiRefunded = 0
//   } = summary;

//   return (
//     <Box px={2} py={1}>
//       <SummaryRow label="Net Cash" value={cashReceived - cashRefunded} />
//       <SummaryRow label="Net Card" value={cardReceived - cardRefunded} />
//       <SummaryRow label="Net UPI" value={upiReceived - upiRefunded} />
//     </Box>
//   );
// }
